import React, { useRef, useEffect } from 'react';
import { useEffectOnce } from 'react-use';
import PropTypes from 'prop-types';
import { importPlaces } from '@artemis/integrations/google';
import styled from 'styled-components';

/*
  Simplified Google autocomplete, no style biases.
  onPlacesChanged is the only unique API, otherwise just enhances an input tag,
  passing through all props
*/
const GoogleMapsAutocomplete = ({
  onPlacesChanged,
  onChange,
  className,
  showSearchIcon,
  ...props
}) => {
  const inputRef = useRef();

  useEffectOnce(() => {
    importPlaces();
  });

  useEffect(() => {
    const input = inputRef.current;
    const gmpSelect = async ({ placePrediction }) => {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: [
          'displayName',
          'formattedAddress',
          'location',
          'addressComponents',
        ],
      });

      onPlacesChanged?.(place);

      input.dispatchEvent(new Event('input'));
    };

    const click = () => {
      requestAnimationFrame(() => {
        if (input.value === '') {
          input.dispatchEvent(new Event('input'));
        }
      });
    };

    input.addEventListener('gmp-select', gmpSelect);
    input.addEventListener('input', onChange);
    input.addEventListener('click', click);
    return () => {
      input.removeEventListener('gmp-select', gmpSelect);
      input.removeEventListener('input', onChange);
      input.removeEventListener('click', click);
    };
  }, [inputRef, onChange]);

  return (
    <gmp-place-autocomplete
      no-input-icon={showSearchIcon ? undefined : 'true'}
      data-testid="google-maps-autocomplete"
      ref={inputRef}
      class={className}
      {...props}
    ></gmp-place-autocomplete>
  );
};

GoogleMapsAutocomplete.propTypes = {
  onPlacesChanged: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
  showSearchIcon: PropTypes.bool,
};

export default styled(GoogleMapsAutocomplete)`
  border-radius: 8px;
  border: none;
  background: white;
  &::part(input) {
    background: transparent;
    color: ${props => props.theme.palette.text.regular.primary};
  }
`;
